import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SignupModal from '../../components/feature/SignupModal';

export default function HomePage() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const testimonials = [
    {
      quote: "Appofy has completely transformed how we manage our salon appointments. The automated reminders have reduced no-shows by 80%, and our clients love the seamless booking experience.",
      name: "Sarah Mitchell",
      title: "Owner, Luxe Beauty Salon",
      avatar: "https://public.readdy.ai/ai/img_res/850aac1dc356cd5bd29888008116f53f.jpg"
    },
    {
      quote: "As a medical practice, we needed something reliable and professional. Appofy delivers on every front. The client profiles help us provide personalized care, and the calendar system is incredibly intuitive.",
      name: "Dr. James Chen",
      title: "Medical Director, HealthFirst Clinic",
      avatar: "https://readdy.ai/api/search-image?query=asian%20male%20doctor%20in%20white%20coat%20smiling%20warmly%20professional%20headshot%20against%20light%20blue%20medical%20background&width=120&height=120&seq=testimonial2&orientation=squarish"
    },
    {
      quote: "We manage over 500 appointments weekly across multiple locations. Appofy's multi-calendar sync and team features have made coordination effortless. Best investment we've made this year.",
      name: "Maria Rodriguez",
      title: "Operations Manager, FitLife Gyms",
      avatar: "https://readdy.ai/api/search-image?query=latina%20woman%20with%20long%20dark%20hair%20in%20professional%20blazer%20smiling%20confidently%20corporate%20headshot%20against%20white%20background&width=120&height=120&seq=testimonial3&orientation=squarish"
    }
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: billingCycle === 'monthly' ? 29 : 24,
      description: 'Perfect for small businesses and solo practitioners',
      features: [
        'Up to 100 appointments/month',
        'Single calendar view',
        'Email reminders',
        'Basic client profiles',
        'Mobile app access',
        'Email support'
      ],
      cta: 'Start Free Trial',
      popular: false
    },
    {
      name: 'Professional',
      price: billingCycle === 'monthly' ? 79 : 65,
      description: 'Ideal for growing businesses with multiple staff',
      features: [
        'Unlimited appointments',
        'Multiple calendar sync',
        'SMS + Email + Push reminders',
        'Advanced client profiles',
        'Team collaboration tools',
        'Priority support',
        'Custom branding',
        'Analytics dashboard'
      ],
      cta: 'Get Started',
      popular: true
    },
    {
      name: 'Enterprise',
      price: null,
      description: 'Custom solutions for large organizations',
      features: [
        'Everything in Professional',
        'Multi-location management',
        'API access',
        'Custom integrations',
        'Dedicated account manager',
        'Advanced security features',
        'SLA guarantee',
        'White-label options'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 h-18">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center gap-2">
              <img src="https://public.readdy.ai/ai/img_res/0e5ab87b-aaca-43fd-8347-1dda3c04dee7.png" alt="Appofy Logo" className="h-10 w-10" />
              <span className="text-2xl font-bold text-gray-900">Appofy</span>
            </div>
            
            <div className="hidden md:flex items-center gap-10">
              <a href="#home" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Home</a>
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Features</a>
              <a href="#calendars" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Calendars</a>
              <a href="#reminders" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Reminders</a>
              <a href="#profiles" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Client Profiles</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Pricing</a>
            </div>

            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                Sign In
              </button>
              <button 
                onClick={() => setIsSignupModalOpen(true)}
                className="bg-teal-500 text-white px-6 py-2.5 rounded-lg hover:bg-teal-600 transition-all hover:shadow-lg whitespace-nowrap cursor-pointer"
              >
                Get Started Free
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-teal-50/50 to-white"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-star-fill text-teal-600"></i>
                Trusted by 10,000+ businesses
              </div>
              
              <h1 className="text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Schedule Smarter,<br />Not Harder
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Appofy streamlines your appointment management with intelligent calendars, automated reminders, and comprehensive client profiles—all in one powerful platform.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => setIsSignupModalOpen(true)}
                  className="bg-teal-500 text-white px-8 py-4 rounded-lg hover:bg-teal-600 transition-all hover:shadow-xl hover:scale-105 text-lg font-semibold whitespace-nowrap cursor-pointer"
                >
                  Start Free Trial
                </button>
                <button className="bg-white text-teal-600 border-2 border-teal-500 px-8 py-4 rounded-lg hover:bg-teal-50 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
                  <i className="ri-play-circle-line text-xl"></i>
                  Watch Demo
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="w-full h-full flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20appointment%20scheduling%20software%20dashboard%20interface%20showing%20colorful%20calendar%20grid%20with%20multiple%20appointment%20blocks%20client%20profiles%20and%20notification%20cards%20clean%20white%20interface%20with%20teal%20accents%20professional%20business%20software%20mockup&width=600&height=500&seq=hero1&orientation=landscape" 
                  alt="Appofy Dashboard" 
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-sm font-bold text-orange-500 uppercase tracking-wider mb-4">FEATURES</div>
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Everything You Need to Manage Appointments</h2>
            <p className="text-xl text-gray-600">Powerful tools designed for modern businesses</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer">
              <div className="w-14 h-14 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-calendar-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Smart Calendars</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Customizable calendar views with drag-and-drop scheduling, color-coding, and multi-calendar sync. Manage your time efficiently with intelligent conflict detection.
              </p>
              <a href="#calendars" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center gap-2 cursor-pointer">
                Learn more <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 relative overflow-hidden cursor-pointer">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <i className="ri-notification-3-line"></i>
                  Reminders
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">Never Miss an Appointment</h3>
                <p className="text-white/90 mb-6 leading-relaxed">
                  Automated SMS, email, and push notifications keep everyone on track. Reduce no-shows by up to 80% with smart reminder sequences.
                </p>
                <button className="bg-white text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer">
                  Explore <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 cursor-pointer">
              <div className="mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=customer%20relationship%20management%20interface%20showing%20organized%20client%20profile%20cards%20with%20avatars%20contact%20information%20and%20appointment%20history%20clean%20modern%20crm%20dashboard%20with%20teal%20accents&width=400&height=200&seq=feature1&orientation=landscape" 
                  alt="Client Profiles" 
                  className="w-full h-40 object-cover object-top rounded-xl mb-6"
                />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Client Profiles</h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive client management with appointment history, preferences, notes, and custom fields. Build stronger relationships with detailed insights.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Calendars Deep-Dive Section */}
      <section id="calendars" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-2">
              <div className="text-sm font-bold text-orange-500 uppercase tracking-wider mb-4">CALENDARS</div>
              <h2 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">Visualize Your Schedule Your Way</h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex gap-4">
                  <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Multiple View Options</h4>
                    <p className="text-gray-600">Switch between day, week, month, and agenda views instantly</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Drag & Drop Scheduling</h4>
                    <p className="text-gray-600">Effortlessly reschedule appointments with intuitive controls</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Color-Coded Organization</h4>
                    <p className="text-gray-600">Categorize appointments by type, staff, or custom criteria</p>
                  </div>
                </div>
              </div>
              
              <button className="bg-gray-900 text-white px-8 py-4 rounded-lg hover:bg-gray-800 transition-all hover:shadow-lg whitespace-nowrap cursor-pointer">
                Customize Your View
              </button>
            </div>

            <div className="md:col-span-3">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=interactive%20calendar%20interface%20showing%20monthly%20view%20with%20colorful%20appointment%20blocks%20in%20pastel%20shades%20organized%20grid%20layout%20with%20dates%20and%20multiple%20scheduled%20events%20modern%20scheduling%20software%20design%20clean%20white%20background&width=700&height=600&seq=calendar1&orientation=landscape" 
                  alt="Calendar Views" 
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reminders Showcase Section */}
      <section id="reminders" className="py-24 bg-gradient-to-br from-teal-500 to-teal-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="text-sm font-bold text-teal-100 uppercase tracking-wider mb-4">AUTOMATED REMINDERS</div>
          <h2 className="text-5xl font-bold mb-6">Set It and Forget It</h2>
          <p className="text-xl text-teal-50 mb-16 max-w-3xl mx-auto leading-relaxed">
            Our intelligent reminder system automatically sends notifications via SMS, email, and push notifications. Customize timing, frequency, and messaging to match your business needs.
          </p>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3">Email Reminders</h3>
              <p className="text-teal-50">Professional branded emails with appointment details and calendar attachments</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-message-3-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3">SMS Notifications</h3>
              <p className="text-teal-50">Instant text messages with high open rates and quick confirmation options</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-notification-3-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3">Push Alerts</h3>
              <p className="text-teal-50">Real-time mobile notifications for last-minute changes and updates</p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Profiles Section */}
      <section id="profiles" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=detailed%20client%20profile%20interface%20showing%20customer%20card%20with%20avatar%20photo%20contact%20information%20appointment%20history%20timeline%20color%20coded%20status%20tags%20and%20notes%20section%20modern%20crm%20software%20design%20with%20teal%20accents%20clean%20organized%20layout&width=600&height=550&seq=profile1&orientation=portrait" 
                alt="Client Profile Interface" 
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>

            <div>
              <div className="text-sm font-bold text-orange-500 uppercase tracking-wider mb-4">CLIENT PROFILES</div>
              <h2 className="text-5xl font-bold text-gray-900 mb-8">Know Your Clients Better</h2>
              
              <div className="space-y-6 mb-8">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-heart-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg mb-2">Complete History</h4>
                    <p className="text-gray-600">Track every appointment, interaction, and preference in one place</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-3-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg mb-2">Custom Fields</h4>
                    <p className="text-gray-600">Add unlimited custom fields to capture business-specific information</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-sticky-note-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg mb-2">Smart Notes</h4>
                    <p className="text-gray-600">Add private notes and tags for personalized service delivery</p>
                  </div>
                </div>
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-lg mb-8">
                <p className="text-teal-900 font-semibold text-lg">Track 50+ data points per client</p>
              </div>
              
              <a href="#features" className="text-teal-600 font-semibold text-lg hover:text-teal-700 inline-flex items-center gap-2 cursor-pointer">
                See All Features <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="text-sm font-bold text-orange-500 uppercase tracking-wider mb-4">PRICING</div>
            <h2 className="text-5xl font-bold text-gray-900 mb-8">Plans That Grow With You</h2>
            
            <div className="inline-flex items-center gap-4 bg-gray-100 p-2 rounded-full">
              <button 
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
              >
                Monthly
              </button>
              <button 
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${billingCycle === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
              >
                Yearly
              </button>
              {billingCycle === 'yearly' && (
                <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
                  Save 20%
                </span>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-start">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index}
                className={`rounded-3xl p-8 transition-all hover:shadow-2xl ${
                  plan.popular 
                    ? 'bg-teal-500 text-white transform md:-translate-y-4 shadow-xl' 
                    : 'bg-white border-2 border-gray-200 hover:-translate-y-2'
                }`}
              >
                {plan.popular && (
                  <div className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold inline-block mb-4">
                    Most Popular
                  </div>
                )}
                
                <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                
                <div className="mb-4">
                  {plan.price ? (
                    <div className="flex items-baseline gap-1">
                      <span className={`text-5xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                        ${plan.price}
                      </span>
                      <span className={`text-lg ${plan.popular ? 'text-teal-100' : 'text-gray-500'}`}>
                        /{billingCycle === 'monthly' ? 'month' : 'year'}
                      </span>
                    </div>
                  ) : (
                    <div className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                      Custom Pricing
                    </div>
                  )}
                </div>
                
                <p className={`mb-8 ${plan.popular ? 'text-teal-50' : 'text-gray-600'}`}>
                  {plan.description}
                </p>
                
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className={`ri-check-line text-xl flex-shrink-0 ${plan.popular ? 'text-white' : 'text-teal-500'}`}></i>
                      <span className={plan.popular ? 'text-teal-50' : 'text-gray-600'}>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button 
                  onClick={() => plan.name === 'Enterprise' ? null : setIsSignupModalOpen(true)}
                  className={`w-full py-4 rounded-lg font-semibold transition-all hover:shadow-lg whitespace-nowrap cursor-pointer ${
                    plan.popular 
                      ? 'bg-white text-teal-600 hover:bg-gray-50' 
                      : 'bg-teal-500 text-white hover:bg-teal-600'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-6">WHAT OUR CLIENTS SAY</div>
            <div className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-full mb-8">
              <i className="ri-star-fill text-orange-500"></i>
              <span className="font-bold text-lg">4.9/5</span>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-12 shadow-lg relative">
            <i className="ri-double-quotes-l text-6xl text-teal-500 opacity-20 absolute top-8 left-8"></i>
            
            <p className="text-2xl text-gray-700 mb-8 leading-relaxed relative z-10 italic">
              {testimonials[currentTestimonial].quote}
            </p>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src={testimonials[currentTestimonial].avatar} 
                  alt={testimonials[currentTestimonial].name}
                  className="w-14 h-14 rounded-full object-cover"
                />
                <div>
                  <div className="font-bold text-gray-900 text-lg">{testimonials[currentTestimonial].name}</div>
                  <div className="text-gray-600">{testimonials[currentTestimonial].title}</div>
                </div>
              </div>

              <div className="flex gap-2">
                <button 
                  onClick={() => setCurrentTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))}
                  className="w-12 h-12 border-2 border-gray-300 rounded-lg flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                </button>
                <button 
                  onClick={() => setCurrentTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))}
                  className="w-12 h-12 bg-gray-900 text-white rounded-lg flex items-center justify-center hover:bg-gray-800 transition-all cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, transparent 0%, transparent 50%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.1) 100%)',
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-5 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/0e5ab87b-aaca-43fd-8347-1dda3c04dee7.png" alt="Appofy Logo" className="h-10 w-10" />
                <span className="text-2xl font-bold">Appofy</span>
              </div>
              <p className="text-3xl font-bold text-white mb-6 leading-tight">Scheduling Made Simple</p>
              <div className="flex gap-2">
                <input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-teal-500"
                />
                <button className="bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-notification-3-line text-xl"></i>
                </button>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Product</h4>
              <ul className="space-y-3">
                <li><a href="#features" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Features</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Pricing</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Integrations</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Company</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">About Us</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Careers</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Resources</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Blog</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Help Center</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">API Docs</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-gray-400 text-sm">
              © 2025 Appofy. All rights reserved.
            </div>

            <div className="flex gap-4">
              <a href="#" className="w-10 h-10 border border-white/20 rounded-lg flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-all cursor-pointer">
                <i className="ri-linkedin-fill text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 border border-white/20 rounded-lg flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-all cursor-pointer">
                <i className="ri-twitter-x-line text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 border border-white/20 rounded-lg flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-all cursor-pointer">
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 border border-white/20 rounded-lg flex items-center justify-center hover:border-teal-500 hover:text-teal-500 transition-all cursor-pointer">
                <i className="ri-instagram-line text-lg"></i>
              </a>
            </div>

            <div className="flex items-center gap-4 text-sm">
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
              <span className="text-gray-600">|</span>
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Terms of Service</a>
              <span className="text-gray-600">|</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>

      <SignupModal isOpen={isSignupModalOpen} onClose={() => setIsSignupModalOpen(false)} />
    </div>
  );
}
